import { useState, useEffect } from 'react';
import { Plus, Search } from 'lucide-react';
import MenuItemCard from '@/components/admin/MenuItemCard';
import MenuReadinessPanel from '@/components/admin/MenuReadinessPanel';
import CanteenVisibilityCard from '@/components/admin/CanteenVisibilityCard';
import MenuItemFormDialog, { MENU_TYPES } from '@/components/admin/MenuItemFormDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { API_URL } from '@/config/api';

export default function AdminMenu() {
  const { token } = useAuth();
  const [menuItems, setMenuItems] = useState([]);
  const [categoryStatus, setCategoryStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [editingItem, setEditingItem] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [availableSides, setAvailableSides] = useState([]);

  useEffect(() => {
    fetchMenu();
    fetchCategoryStatus();
    fetchAvailableSides();
  }, [token]);

  const fetchAvailableSides = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/sides`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setAvailableSides(await res.json());
    } catch (error) {
      console.error('Error fetching sides:', error);
    }
  };

  const fetchCategoryStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/menu/category-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setCategoryStatus(await res.json());
    } catch (error) {
      console.error('Error fetching category status:', error);
    }
  };

  const fetchMenu = async () => {
    try {
      const res = await fetch(`${API_URL}/api/menu`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setMenuItems(await res.json());
    } catch (error) {
      console.error('Error fetching menu:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaved = () => {
    setEditingItem(null);
    fetchMenu();
    fetchCategoryStatus();
  };

  const handleDelete = async (itemId) => {
    if (!confirm('Are you sure you want to delete this item?')) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/menu/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to delete item');
      toast.success('Item deleted');
      fetchMenu();
      fetchCategoryStatus();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setIsDialogOpen(true);
  };

  const openNewDialog = () => {
    setEditingItem(null);
    setIsDialogOpen(true);
  };

  const filteredItems = menuItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || item.menu_type === filterType;
    return matchesSearch && matchesType;
  });

  return (
    <div data-testid="admin-menu">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-secondary-900">Menu Management</h1>
          <p className="text-sm text-muted-foreground">Add, edit, and manage menu items</p>
        </div>
        <Button onClick={openNewDialog} className="bg-primary hover:bg-primary-600" data-testid="add-item-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </Button>
      </div>

      <CanteenVisibilityCard />

      <MenuReadinessPanel categoryStatus={categoryStatus} />

      {/* Search & Filter */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search menu items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-menu"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {MENU_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Menu Items */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : filteredItems.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {filteredItems.map(item => (
            <MenuItemCard
              key={item.item_id}
              item={item}
              onEdit={openEditDialog}
              onDelete={handleDelete}
              onUpdate={fetchMenu}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-muted-foreground">No menu items found</p>
          </CardContent>
        </Card>
      )}

      <MenuItemFormDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        editingItem={editingItem}
        availableSides={availableSides}
        onSaved={handleSaved}
      />
    </div>
  );
}
